// Packages
import {
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Listen,
  Method,
  Prop,
  State,
  Watch,
} from "@stencil/core";

// Types
import { SegmentChangeEventDetail } from "src/interface";

interface FxSegmentButtonElement extends HTMLElement {
  disabled?: boolean;
  contentId?: string;
  selected?: boolean;
  segmentTabIndex?: number;
  setFocus(): Promise<void>;
}

interface FxSegmentViewElement extends HTMLElement {
  setActive(contentId: string): Promise<void>;
}

/**
 * `fx-segment` is a headless tab bar that controls an `fx-segment-view`. It
 * exposes state via `data-*` attributes, supports roving `tabindex` keyboard
 * navigation, optional scroll and swipe behaviors, and emits `fxChange` when
 * the active segment changes.
 *
 * @slot - The `fx-segment-button` tabs.
 *
 * @part container - The scroll wrapper around the slotted segment buttons.
 * @part indicator - The visual selection indicator element.
 */
// `styleUrl` is intentionally omitted (no internal styles per AGENTS.md).
@Component({
  tag: "fx-segment",
  shadow: true,
})
export class FxSegment {
  // --------------------------------------------
  // Element / internal references
  // --------------------------------------------

  @Element() el!: HTMLElement;

  private buttonOriginalDisabledMap = new WeakMap<FxSegmentButtonElement, boolean>();
  private isSyncing = false;
  private wasSegmentDisabled = false;
  private pointerStartX = 0;
  private pointerStartButton: FxSegmentButtonElement | null = null;
  private isPointerDown = false;
  private isSwiping = false;
  private suppressNextClick = false;
  private activePointerId: number | null = null;
  private containerEl?: HTMLElement;
  private indicatorRaf?: number;
  private resizeObserver?: ResizeObserver;
  private hasFocus = false;

  // --------------------------------------------
  // Props
  // --------------------------------------------

  /**
   * @prop - If `true`, the segment is disabled and cannot be interacted with.
   */
  @Prop()
  disabled: boolean = false;

  /**
   * @prop - If `true`, the button bar is horizontally scrollable.
   */
  @Prop()
  scrollable: boolean = false;

  /**
   * @prop - If `true`, users can swipe between segment buttons to activate
   * adjacent tabs.
   */
  @Prop()
  swipeGesture: boolean = false;

  /**
   * @prop - The size of the segment. Drives the padding, gap, radius and
   * font-size of the container and its buttons. Reflects to `data-size` on the
   * host. Defaults to `md`.
   */
  @Prop()
  size: "xs" | "sm" | "md" | "lg" | "xl" = "md";

  /**
   * @prop - The `id` of the active `fx-segment-content` panel. This is the
   * authoritative active value for the segment group.
   */
  @Prop({ mutable: true })
  value?: string;

  /**
   * @prop - The DOM `id` of the associated `fx-segment-view`.
   */
  @Prop()
  contentId?: string;

  // --------------------------------------------
  // State
  // --------------------------------------------

  /**
   * Index of the button that currently has roving focus. When `-1`, the
   * selected button receives focus by default.
   */
  @State()
  private focusedIndex: number = -1;

  // --------------------------------------------
  // Events
  // --------------------------------------------

  /**
   * @event - Emitted when the active segment value changes.
   */
  @Event()
  fxChange!: EventEmitter<SegmentChangeEventDetail>;

  /**
   * @event - Emitted when the segment gains focus.
   */
  @Event()
  fxFocus!: EventEmitter<void>;

  /**
   * @event - Emitted when the segment loses focus.
   */
  @Event()
  fxBlur!: EventEmitter<void>;

  // --------------------------------------------
  // Lifecycle
  // --------------------------------------------

  private isLoaded = false;

  connectedCallback() {
    this.syncButtons();
    if (this.containerEl) {
      this.observeResizes();
    }
  }

  componentWillLoad() {
    this.focusedIndex = this.getIndexByContentId(this.value);
    this.syncButtons();
  }

  componentDidLoad() {
    this.isLoaded = true;
    this.syncButtons();
    this.observeResizes();

    if (this.contentId && this.value !== undefined) {
      this.getView()?.setActive(this.value);
    }
  }

  componentDidUpdate() {
    this.syncButtons();
  }

  disconnectedCallback() {
    if (this.indicatorRaf !== undefined) {
      cancelAnimationFrame(this.indicatorRaf);
      this.indicatorRaf = undefined;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }
    if (this.activePointerId !== null) {
      try {
        this.el.releasePointerCapture?.(this.activePointerId);
      } catch {
        // Capture may already be lost.
      }
    }
    this.activePointerId = null;
    this.isPointerDown = false;
    this.isSwiping = false;
    this.suppressNextClick = false;
    this.pointerStartButton = null;
    this.hasFocus = false;
  }

  // --------------------------------------------
  // Watchers
  // --------------------------------------------

  @Watch("value")
  async valueChanged(newValue: string | undefined) {
    if (this.isLoaded && !this.isSyncing) {
      await this.syncToValue(newValue, true);
    }
  }

  @Watch("disabled")
  disabledChanged() {
    this.syncButtons();
  }

  // --------------------------------------------
  // Focus management
  // --------------------------------------------

  @Listen("focusin", { target: "window" })
  handleFocusIn(ev: FocusEvent) {
    if (!this.el.contains(ev.target as Node)) {
      return;
    }
    if (this.hasFocus) {
      return;
    }
    this.hasFocus = true;
    this.fxFocus.emit();
  }

  @Listen("focusout", { target: "window" })
  handleFocusOut(ev: FocusEvent) {
    if (!this.el.contains(ev.target as Node)) {
      return;
    }
    if (this.el.contains(ev.relatedTarget as Node)) {
      return;
    }
    this.hasFocus = false;
    this.fxBlur.emit();
  }

  // --------------------------------------------
  // Listeners
  // --------------------------------------------

  @Listen("fxSegmentButtonActivate")
  onButtonActivate(ev: CustomEvent<{ contentId?: string }>) {
    if (this.disabled) {
      return;
    }

    const contentId = ev.detail?.contentId;
    if (contentId !== undefined) {
      this.setActive(contentId);
    }
  }

  @Listen("fxSegmentButtonKeydown")
  onButtonKeydown(ev: CustomEvent<{ key: string; contentId?: string }>) {
    if (this.disabled) {
      return;
    }

    const target = ev.target as FxSegmentButtonElement;
    const buttons = this.getButtons();
    const index = buttons.indexOf(target);
    if (index === -1) {
      return;
    }

    const key = ev.detail?.key;

    switch (key) {
      case "ArrowRight":
      case "ArrowLeft":
      case "Home":
      case "End": {
        const nextIndex = this.getNextIndex(index, key, buttons);
        if (nextIndex !== -1) {
          this.focusedIndex = nextIndex;
          this.syncButtons();
          buttons[nextIndex].setFocus?.();
        }
        break;
      }
      case " ":
      case "Enter":
      case "Spacebar": {
        const contentId = ev.detail?.contentId;
        if (contentId !== undefined) {
          this.setActive(contentId);
        }
        break;
      }
    }
  }

  @Listen("fxSegmentViewChange", { target: "window" })
  async onViewChange(ev: CustomEvent<SegmentChangeEventDetail>) {
    if (this.disabled) {
      return;
    }

    const view = this.getView();
    if (!view || ev.target !== view) {
      return;
    }

    const contentId = ev.detail?.value;
    if (contentId === undefined || contentId === this.value) {
      return;
    }

    await this.setActive(contentId);
    this.focusActiveButton();
  }

  // --------------------------------------------
  // Public methods
  // --------------------------------------------

  /**
   * @method - Sets the active segment by `contentId`, updates the linked
   * `fx-segment-view`, synchronizes button states, and emits `fxChange`.
   */
  @Method()
  async setActive(contentId: string): Promise<void> {
    if (this.disabled || contentId === this.value) {
      return;
    }

    this.syncToValue(contentId, true);
  }

  // --------------------------------------------
  // Private helpers
  // --------------------------------------------

  private handleSlotChange = () => {
    this.syncButtons();
    this.scheduleIndicatorUpdate();
  };

  private handleContainerScroll = () => {
    this.scheduleIndicatorUpdate();
  };

  private syncToValue(value: string | undefined, emit: boolean) {
    if (this.isSyncing || value === this.value) {
      return;
    }

    this.isSyncing = true;
    this.value = value;
    this.focusedIndex = this.getIndexByContentId(value);

    this.syncButtons();

    if (emit) {
      this.fxChange.emit({ value: this.value });
    }

    this.focusActiveButton();

    this.isSyncing = false;

    const view = this.getView();
    if (view && value !== undefined) {
      // Fire-and-forget the view transition so the segment remains responsive
      // while the panel animation plays.
      view.setActive(value).catch(() => {
        // Transition may be cancelled or the view may be detached.
      });
    }
  }

  private get hostAriaLabel(): string {
    return this.el.getAttribute("aria-label") || "Tab list";
  }

  private getButtons(): FxSegmentButtonElement[] {
    return Array.from(this.el.querySelectorAll("fx-segment-button")) as FxSegmentButtonElement[];
  }

  private focusActiveButton() {
    const selected = this.getButtons().find(
      (button) => button.contentId === this.value && !button.disabled
    );
    selected?.setFocus?.();
  }

  private getIndexByContentId(contentId?: string): number {
    if (!contentId) {
      return -1;
    }

    return this.getButtons().findIndex((button) => button.contentId === contentId);
  }

  private getNextIndex(current: number, key: string, buttons: FxSegmentButtonElement[]): number {
    const enabledIndices = buttons
      .map((button, index) => ({ button, index }))
      .filter(({ button }) => !button.disabled)
      .map(({ index }) => index);

    if (enabledIndices.length === 0) {
      return -1;
    }

    if (key === "Home") {
      return enabledIndices[0];
    }

    if (key === "End") {
      return enabledIndices[enabledIndices.length - 1];
    }

    const currentEnabled = enabledIndices.indexOf(current);
    if (currentEnabled === -1) {
      return enabledIndices[0];
    }

    if (key === "ArrowRight") {
      return enabledIndices[currentEnabled + 1] ?? enabledIndices[currentEnabled];
    }

    return enabledIndices[currentEnabled - 1] ?? enabledIndices[currentEnabled];
  }

  private syncButtons() {
    const buttons = this.getButtons();
    const selectedIndex = this.getIndexByContentId(this.value);

    let focusIndex = this.focusedIndex;
    if (focusIndex === -1 || (focusIndex >= 0 && buttons[focusIndex]?.disabled)) {
      focusIndex = selectedIndex;
    }
    if (focusIndex === -1 || buttons[focusIndex]?.disabled) {
      focusIndex = buttons.findIndex((button) => !button.disabled);
    }

    buttons.forEach((button, index) => {
      if (this.disabled) {
        if (!this.wasSegmentDisabled) {
          this.buttonOriginalDisabledMap.set(button, !!button.disabled);
        }
        button.disabled = true;
      } else {
        if (this.wasSegmentDisabled && this.buttonOriginalDisabledMap.has(button)) {
          button.disabled = this.buttonOriginalDisabledMap.get(button)!;
          this.buttonOriginalDisabledMap.delete(button);
        }
        // Otherwise, respect the button's current disabled state so consumer
        // changes are not overwritten.
      }

      button.selected = index === selectedIndex;
      button.segmentTabIndex = index === focusIndex && !button.disabled ? 0 : -1;
    });

    this.wasSegmentDisabled = this.disabled;
    this.scheduleIndicatorUpdate();
  }

  private scheduleIndicatorUpdate() {
    if (this.indicatorRaf !== undefined) {
      cancelAnimationFrame(this.indicatorRaf);
    }
    this.indicatorRaf = requestAnimationFrame(() => {
      this.indicatorRaf = undefined;
      this.updateIndicator();
    });
  }

  private updateIndicator() {
    if (!this.el.isConnected || !this.containerEl) {
      return;
    }

    const buttons = this.getButtons();
    const selectedIndex = this.getIndexByContentId(this.value);
    if (selectedIndex === -1 || !buttons[selectedIndex]) {
      this.el.style.setProperty("--fx-segment-indicator-left", "0px");
      this.el.style.setProperty("--fx-segment-indicator-width", "0px");
      return;
    }

    const button = buttons[selectedIndex] as HTMLElement;
    const hostRect = this.el.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();

    const left = buttonRect.left - hostRect.left;
    const width = buttonRect.width;

    this.el.style.setProperty("--fx-segment-indicator-left", `${left}px`);
    this.el.style.setProperty("--fx-segment-indicator-width", `${width}px`);
  }

  private observeResizes() {
    if (typeof ResizeObserver === "undefined" || !this.containerEl) {
      return;
    }
    this.resizeObserver = new ResizeObserver(() => this.scheduleIndicatorUpdate());
    this.resizeObserver.observe(this.containerEl);
  }

  private getView(): FxSegmentViewElement | null {
    if (!this.contentId) {
      return null;
    }

    const root = this.el.getRootNode() as Document | ShadowRoot;
    const selector = `fx-segment-view[id="${this.contentId.replace(/"/g, '\\"')}"]`;
    if ("querySelector" in root) {
      const view = (root as any).querySelector(selector);
      return view as FxSegmentViewElement | null;
    }

    return null;
  }

  // --------------------------------------------
  // Swipe handlers
  // --------------------------------------------

  private getButtonFromEvent(ev: PointerEvent): FxSegmentButtonElement | null {
    const node = ev.composedPath().find((n) => {
      if (!(n instanceof HTMLElement)) {
        return false;
      }
      return (n as HTMLElement).tagName === "FX-SEGMENT-BUTTON";
    });
    return (node as FxSegmentButtonElement) || null;
  }

  @Listen("pointerdown")
  handlePointerDown(ev: PointerEvent) {
    if (this.disabled || !this.swipeGesture) {
      return;
    }

    this.isPointerDown = true;
    this.isSwiping = false;
    this.suppressNextClick = false;
    this.activePointerId = ev.pointerId;
    this.pointerStartX = ev.clientX;
    this.pointerStartButton = this.getButtonFromEvent(ev);

    if (this.pointerStartButton && !this.pointerStartButton.disabled) {
      this.pointerStartButton.setFocus?.();
    }
  }

  @Listen("pointermove")
  handlePointerMove(ev: PointerEvent) {
    if (!this.isPointerDown || ev.pointerId !== this.activePointerId || !this.swipeGesture) {
      return;
    }

    const deltaX = ev.clientX - this.pointerStartX;
    const slop = 10;

    if (!this.isSwiping && Math.abs(deltaX) > slop) {
      this.isSwiping = true;
      try {
        this.el.setPointerCapture(ev.pointerId);
      } catch {
        // Capture may already be lost or synthetic.
      }
    }
  }

  @Listen("pointerup")
  async handlePointerUp(ev: PointerEvent) {
    if (!this.isPointerDown || ev.pointerId !== this.activePointerId) {
      return;
    }

    const startButton = this.pointerStartButton;

    this.isPointerDown = false;
    this.activePointerId = null;
    this.pointerStartButton = null;

    try {
      this.el.releasePointerCapture(ev.pointerId);
    } catch {
      // Capture may already be lost.
    }

    if (!this.swipeGesture || this.disabled) {
      return;
    }

    const deltaX = ev.clientX - this.pointerStartX;
    const threshold = 50;

    if (!this.isSwiping || Math.abs(deltaX) < threshold) {
      // Small movement or tap: activate the pressed button.
      this.isSwiping = false;
      if (startButton && !startButton.disabled && startButton.contentId) {
        await this.setActive(startButton.contentId);
        this.focusActiveButton();
      }
      return;
    }

    this.isSwiping = false;
    this.suppressNextClick = true;

    const buttons = this.getButtons().filter((button) => !button.disabled);
    const selectedIndex = buttons.findIndex((button) => button.contentId === this.value);

    if (selectedIndex === -1) {
      return;
    }

    let next: FxSegmentButtonElement | undefined;

    if (deltaX > 0) {
      next = buttons[selectedIndex - 1] ?? buttons[buttons.length - 1];
    } else {
      next = buttons[selectedIndex + 1] ?? buttons[0];
    }

    if (next && next.contentId) {
      await this.setActive(next.contentId);
      this.focusActiveButton();
    }
  }

  @Listen("pointercancel")
  handlePointerCancel(ev: PointerEvent) {
    if (!this.isPointerDown || ev.pointerId !== this.activePointerId) {
      return;
    }

    this.isPointerDown = false;
    this.isSwiping = false;
    this.suppressNextClick = false;
    this.activePointerId = null;
    this.pointerStartButton = null;

    try {
      this.el.releasePointerCapture(ev.pointerId);
    } catch {
      // Capture may already be lost.
    }
  }

  @Listen("click", { capture: true })
  handleClickCapture(ev: MouseEvent) {
    if (!this.suppressNextClick) {
      return;
    }
    this.suppressNextClick = false;
    ev.stopPropagation();
    ev.preventDefault();
  }

  // --------------------------------------------
  // Render
  // --------------------------------------------

  render() {
    const { disabled, scrollable, swipeGesture, value, size } = this;

    return (
      <Host
        role="tablist"
        aria-label={this.hostAriaLabel}
        aria-disabled={disabled ? "true" : null}
        data-disabled={disabled ? "true" : "false"}
        data-scrollable={scrollable ? "true" : "false"}
        data-size={size}
        data-swipe-gesture={swipeGesture ? "true" : "false"}
        data-value={value}
      >
        <div
          part="container"
          ref={(el) => (this.containerEl = el as HTMLElement)}
          onScroll={this.handleContainerScroll}
        >
          <slot onSlotchange={this.handleSlotChange} />
        </div>
        <div part="indicator" aria-hidden="true" />
      </Host>
    );
  }
}
